import React, { useState, useRef, useEffect } from 'react';
import { ReactionType, REACTION_TYPES } from '@/services/reactionsService';

interface ReactionPickerProps {
  onReactionSelect: (reaction: ReactionType) => Promise<void>;
  currentReaction?: ReactionType | null;
  disabled?: boolean;
}

export function ReactionPicker({ 
  onReactionSelect, 
  currentReaction, 
  disabled = false 
}: ReactionPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleReactionClick = async (reactionType: ReactionType) => {
    if (disabled || isLoading) return;

    setIsLoading(true);
    try {
      await onReactionSelect(reactionType);
      setIsOpen(false);
    } catch (error) {
      console.error('Failed to add reaction:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentReactionData = () => {
    return currentReaction ? REACTION_TYPES[currentReaction] : null;
  };

  const currentReactionData = getCurrentReactionData();

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={disabled || isLoading}
        className={`
          inline-flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all duration-200
          ${currentReactionData 
            ? `bg-white border-gray-300 text-gray-900 shadow-sm hover:shadow-md` 
            : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 hover:border-gray-300'
          }
          ${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
        `}
        aria-label={currentReactionData ? `Current reaction: ${currentReactionData.label}` : 'Add reaction'}
      >
        {isLoading ? (
          <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
        ) : currentReactionData ? (
          <>
            <span className="text-lg" role="img" aria-label={currentReactionData.label}>
              {currentReactionData.emoji}
            </span>
            <span className="text-sm font-medium">{currentReactionData.label}</span>
          </>
        ) : (
          <>
            <span className="text-lg">😊</span>
            <span className="text-sm font-medium">React</span>
          </>
        )}
        <svg 
          className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200 py-3">
          <div className="px-4 py-2 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">Choose a reaction</h3>
            <p className="text-xs text-gray-500 mt-1">Express how you feel about this item</p>
          </div>
          
          <div className="grid grid-cols-2 gap-2 p-3">
            {Object.entries(REACTION_TYPES).map(([key, reaction]) => {
              const reactionType = key as ReactionType;
              const isSelected = currentReaction === reactionType;
              
              return (
                <button
                  key={reactionType}
                  onClick={() => handleReactionClick(reactionType)}
                  disabled={disabled || isLoading}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-left
                    ${isSelected 
                      ? 'bg-blue-50 border border-blue-200 text-blue-900' 
                      : 'hover:bg-gray-50 border border-transparent'
                    }
                    ${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
                  `}
                  style={{ 
                    borderColor: isSelected ? reaction.color : undefined,
                    backgroundColor: isSelected ? `${reaction.color}10` : undefined 
                  }}
                >
                  <span className="text-xl" role="img" aria-label={reaction.label}>
                    {reaction.emoji}
                  </span>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">
                      {reaction.label}
                    </div>
                  </div>
                  {isSelected && (
                    <svg className="w-4 h-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
          
          {currentReaction && (
            <div className="border-t border-gray-100 px-4 py-3">
              <button
                onClick={() => handleReactionClick(currentReaction)}
                disabled={disabled || isLoading}
                className="text-sm text-red-600 hover:text-red-700 font-medium focus:outline-none focus:underline"
              >
                Remove reaction
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ReactionSummary({ 
  reactionSummary, 
  className = "" 
}: { 
  reactionSummary: any; 
  className?: string; 
}) {
  if (!reactionSummary || reactionSummary.totalReactions === 0) {
    return null;
  }

  const topReactions = Object.entries(reactionSummary.reactionCounts || {})
    .sort(([,a], [,b]) => (b as number) - (a as number))
    .slice(0, 3)
    .filter(([, count]) => (count as number) > 0);

  if (topReactions.length === 0) {
    return null;
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="flex items-center gap-1">
        {topReactions.map(([reactionType, count]) => {
          const reaction = REACTION_TYPES[reactionType as ReactionType];
          if (!reaction) return null;
          
          return (
            <div
              key={reactionType}
              className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-full text-sm"
              title={`${count} ${reaction.label} reaction${count === 1 ? '' : 's'}`}
            >
              <span role="img" aria-label={reaction.label}>
                {reaction.emoji}
              </span>
              <span className="text-gray-600 font-medium">{count as number}</span>
            </div>
          );
        })}
      </div>
      
      {reactionSummary.totalReactions > topReactions.reduce((sum, [, count]) => sum + (count as number), 0) && (
        <span className="text-xs text-gray-500">
          +{reactionSummary.totalReactions - topReactions.reduce((sum, [, count]) => sum + (count as number), 0)} more
        </span>
      )}
    </div>
  );
}